import type { Practitioner } from '@medplum/fhirtypes';
import { EXTENSIONS } from './constants';

/**
 * The colors a practice's clinicians are told apart by on the Appointments grid, in the order they are
 * handed out. Hex rather than CSS variables, because the value is stored and has to survive FHIR.
 *
 * Each entry must carry {@link PRACTITIONER_APPOINTMENT_COLOR_CONTRAST} at WCAG AA — a block prints a
 * patient name over the fill at 12px — and is the same in light and dark, a solid fill being its own
 * background. Both are enforced by test rather than assumed here.
 */
export const PRACTITIONER_APPOINTMENT_COLORS = [
  '#268457',
  '#3f5fd0',
  '#8b4fc9',
  '#c62b4f',
  '#c05423',
  '#677d19',
  '#357e92',
  '#b44a9c',
  '#d33a5e',
  '#d14135',
] as const;

export type PractitionerAppointmentColor = (typeof PRACTITIONER_APPOINTMENT_COLORS)[number];

/** The text color every palette entry above is chosen to carry. */
export const PRACTITIONER_APPOINTMENT_COLOR_CONTRAST = '#ffffff';

/** The color assigned to this clinician, or `undefined` for one nothing has colored yet. */
export const practitionerAppointmentColor = (practitioner: Practitioner): string | undefined =>
  practitioner.extension?.find((extension) => extension.url === EXTENSIONS.PRACTITIONER_APPOINTMENT_COLOR)?.valueString;

/**
 * The palette entry to give a clinician whose colleagues already hold `taken`: the least-used, ties
 * breaking toward palette order.
 *
 * That is "the first unused color" while any is unused, and past ten clinicians it spreads the
 * unavoidable collisions rather than piling them onto one color.
 */
export const pickPractitionerAppointmentColor = (taken: Iterable<string | undefined>): PractitionerAppointmentColor => {
  // Seeded with every palette entry, which is what makes a `get` below always resolve — and what
  // silently drops anything outside the palette.
  const counts = new Map<string, number>(PRACTITIONER_APPOINTMENT_COLORS.map((color) => [color, 0]));
  for (const color of taken) {
    if (color === undefined) continue;
    const count = counts.get(color);
    if (count === undefined) continue;
    counts.set(color, count + 1);
  }

  // Strict `<`, so the earliest palette entry wins a tie. No seed value: the palette is a non-empty
  // literal, so `reduce` cannot see an empty array here.
  return PRACTITIONER_APPOINTMENT_COLORS.reduce((best, color) =>
    (counts.get(color) ?? 0) < (counts.get(best) ?? 0) ? color : best,
  );
};
